import { isNil } from 'lodash';
import { useRef } from 'react';
/**
 * Keep input cursor in the correct position if possible.
 */
export function useCursor(
  input: HTMLInputElement | null,
  focused: boolean,
): [() => void, () => void] {
  const selectionRef = useRef<{
    start?: number;
    end?: number;
    value?: string;
    beforeTxt?: string;
    afterTxt?: string;
  } | null>(null);

  function recordCursor() {
    // Record position
    try {
      if (!input) return;
      const { selectionStart: start, selectionEnd: end, value } = input;
      if (isNil(start) || isNil(end)) return;
      const beforeTxt = value.substring(0, start);
      const afterTxt = value.substring(end);

      selectionRef.current = {
        start,
        end,
        value,
        beforeTxt,
        afterTxt,
      };
    } catch (e) {
      // Fix error in Chrome:
      // Failed to read the 'selectionStart' property from 'HTMLInputElement'
      // http://stackoverflow.com/q/21177489/3040605
    }
  }

  /**
   * Restore logic:
   *  1. back string same
   *  2. start string same
   */
  function restoreCursor() {
    if (input && selectionRef.current && focused) {
      try {
        const { value } = input;
        const { beforeTxt, afterTxt, start } = selectionRef.current;

        if (isNil(start) || isNil(afterTxt) || isNil(beforeTxt)) return;

        let startPos = value.length;

        if (value.startsWith(beforeTxt)) {
          startPos = beforeTxt.length;
        } else if (value.endsWith(afterTxt)) {
          startPos = value.length - (selectionRef?.current?.afterTxt?.length || 0);
        } else {
          const beforeLastChar = beforeTxt[start - 1];
          const newIndex = value.indexOf(beforeLastChar, start - 1);
          if (newIndex !== -1) {
            startPos = newIndex + 1;
          }
        }

        input.setSelectionRange(startPos, startPos);
      } catch (e) {
        console.log(e);
      }
    }
  }

  return [recordCursor, restoreCursor];
}
